import React, { useState, useEffect } from 'react';
import { invoicesService, companiesService } from '../services/api';
import InvoicePreviewDialog from '../components/InvoicePreviewDialog';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  PictureAsPdf as PdfIcon,
  Euro as EuroIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  TableView as CsvIcon,
  GridOn as ExcelIcon,
  HowToReg as SignIcon
} from '@mui/icons-material';
import { catalogPresets, suggestCatalogSector } from '../utils/catalogPresets';
import { loadCompanyProfile } from '../utils/companyProfileStorage';
import { loadActivityMode } from '../utils/activityModeStorage';
import { getDefaultInvoiceItemsForActivity } from '../utils/activityDefaults';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signingInvoice, setSigningInvoice] = useState(null);
  const [signForm, setSignForm] = useState({ name: '', email: '', role: '' });
  const [signLoading, setSignLoading] = useState(false);
  const [signError, setSignError] = useState('');
  const [signSuccess, setSignSuccess] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: '',
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
    company: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    number: '',
    companyId: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'DRAFT',
    notes: '',
    items: []
  });
  const [items, setItems] = useState([
    {
      id: Date.now(),
      description: '',
      quantity: 1,
      unit: 'unité',
      unitPrice: 0,
      discount: 0,
      vatRate: 20,
      totalHT: 0,
      totalTTC: 0
    }
  ]);
  const [globalVatZero, setGlobalVatZero] = useState(false);
  const [selectedSector, setSelectedSector] = useState(() => loadActivityMode()?.presetCatalogId || 'services');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [defaultVatRate, setDefaultVatRate] = useState(20);
  const [plumberUrgence, setPlumberUrgence] = useState(false);
  const [totals, setTotals] = useState({
    subtotalHT: 0,
    vatAmount: 0,
    totalTTC: 0
  });

  // Charger les factures et clients
  const loadData = async () => {
    try {
      // Charger les clients d'abord
      const companiesData = await companiesService.getAll();
      setCompanies(companiesData);

      // Charger les factures depuis l'API
      const invoicesData = await invoicesService.getAll();
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (openDialog) {
      const profile = loadCompanyProfile();
      const liable = profile?.vatLiable !== undefined ? profile.vatLiable : true;
      const defaultRate = profile?.defaultVatRate !== undefined ? profile.defaultVatRate : 20;
      setDefaultVatRate(liable ? defaultRate : 0);
      setGlobalVatZero(!liable);
      setSelectedSector(loadActivityMode()?.presetCatalogId || 'services');
      setCatalogSearch('');
      setSelectedCatalogIds([]);
      setAssistantInput('');
      setPlumberUrgence(false);
    }
  }, [openDialog]);

  useEffect(() => {
    if (!openDialog) return;
    if (selectedSector !== 'plumber') {
      setPlumberUrgence(false);
      return;
    }
  }, [openDialog, selectedSector]);

  useEffect(() => {
    if (!openDialog) return;

    if (!plumberUrgence) {
      setItems((prev) => prev.filter((item) => !item?.__autoUrgence));
      return;
    }

    setItems((prev) => {
      if (prev.some((item) => item?.__autoUrgence)) return prev;
      const urgence = {
        id: Date.now() + Math.random(),
        description: 'Majoration urgence',
        quantity: 1,
        unit: 'forfait',
        unitPrice: 60,
        discount: 0,
        vatRate: globalVatZero ? 0 : defaultVatRate,
        totalHT: 0,
        totalTTC: 0,
        __autoUrgence: true,
      };
      const totals = calculateItemTotal(urgence);
      return [...prev, { ...urgence, ...totals }];
    });
  }, [openDialog, plumberUrgence, globalVatZero, defaultVatRate]);

  useEffect(() => {
    let subtotalHT = 0;
    let vatAmount = 0;

    items.forEach(item => {
      let itemSubtotal = item.quantity * item.unitPrice;
      if (item.discount && item.discount > 0) {
        itemSubtotal = itemSubtotal * (1 - item.discount / 100);
      }
      const itemVat = itemSubtotal * (item.vatRate / 100);
      subtotalHT += itemSubtotal;
      vatAmount += itemVat;
    });

    const totalTTC = subtotalHT + vatAmount;
    setTotals({ subtotalHT, vatAmount, totalTTC });
  }, [items]);

  // Télécharger le PDF d'une facture
  const handleDownloadPDF = async (invoice) => {
    try {
      const blob = await invoicesService.downloadPdf(invoice.id);
      downloadBlob(blob, `Facture-${invoice.number}.pdf`);
    } catch (err) {
      logger.error('Erreur téléchargement PDF facture:', err);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCsv = async (invoice) => {
    try {
      const blob = await invoicesService.exportCsv(invoice.id);
      downloadBlob(blob, `Facture-${invoice.number}.csv`);
    } catch (error) {
      logger.error('Erreur export CSV:', error);
      alert("Erreur lors de l'export CSV");
    }
  };

  const handleExportExcel = async (invoice) => {
    try {
      const blob = await invoicesService.exportExcel(invoice.id);
      downloadBlob(blob, `Facture-${invoice.number}.xls`);
    } catch (error) {
      logger.error('Erreur export Excel:', error);
      alert("Erreur lors de l'export Excel");
    }
  };

  const handleOpenSignDialog = (invoice) => {
    const storedUser = localStorage.getItem('user');
    const currentUser = storedUser ? JSON.parse(storedUser) : null;
    setSigningInvoice(invoice);
    setSignForm({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      role: currentUser?.teamRole || ''
    });
    setSignError('');
    setSignSuccess('');
    setSignDialogOpen(true);
  };

  const handleCloseSignDialog = () => {
    setSignDialogOpen(false);
    setSigningInvoice(null);
    setSignLoading(false);
    setSignError('');
    setSignSuccess('');
  };

  const handleSignInvoice = async () => {
    if (!signingInvoice) return;
    if (!signForm.name || !signForm.email) {
      setSignError('Nom et email requis');
      return;
    }

    setSignLoading(true);
    setSignError('');
    setSignSuccess('');

    try {
      await invoicesService.signInvoice(signingInvoice.id, {
        name: signForm.name,
        email: signForm.email,
        role: signForm.role
      });
      setSignSuccess('Facture signée avec succès');
      await loadData();
      setTimeout(() => handleCloseSignDialog(), 1000);
    } catch (error) {
      console.error('Erreur signature:', error);
      setSignError("Erreur lors de la signature");
    } finally {
      setSignLoading(false);
    }
  };

  // Ouvrir le modal d'envoi d'email
  const handleOpenEmailDialog = (invoice) => {
    setEmailInvoice(invoice);
    setRecipientEmail(invoice.company?.email || '');
    setEmailSuccess('');
    setEmailError('');
    setOpenEmailDialog(true);
  };

  // Fermer le modal d'envoi d'email
  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailInvoice(null);
    setRecipientEmail('');
    setEmailSending(false);
    setEmailSuccess('');
    setEmailError('');
  };

  // Envoyer la facture par email
  const handleSendEmail = async () => {
    if (!recipientEmail) {
      setEmailError('Veuillez saisir une adresse email');
      return;
    }

    setEmailSending(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const data = await invoicesService.sendEmail(emailInvoice.id, recipientEmail);
      if (data) {
        setEmailSuccess(`✅ Facture envoyée avec succès à ${recipientEmail}`);
        if (data.previewUrl) {
          logger.debug('🔗 Lien de prévisualisation:', data.previewUrl);
        }
        setTimeout(() => {
          handleCloseEmailDialog();
        }, 2000);
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      const message = error?.response?.data?.error || error?.response?.data?.message || "Erreur lors de l'envoi de l'email";
      setEmailError(message);
    } finally {
      setEmailSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SENT': return 'primary';
      case 'PAID': return 'success';
      case 'OVERDUE': return 'error';
      case 'CANCELLED': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT': return 'Brouillon';
      case 'SENT': return 'Envoyée';
      case 'PAID': return 'Payée';
      case 'OVERDUE': return 'En retard';
      case 'CANCELLED': return 'Annulée';
      default: return status;
    }
  };

  const isOverdue = (invoice) => {
    if (invoice.status === 'PAID' || invoice.status === 'CANCELLED') return false;
    return new Date(invoice.dueDate) < new Date();
  };

  const handleOpenDialog = (invoice = null) => {
    const profile = loadCompanyProfile();
    const liable = profile?.vatLiable !== undefined ? profile.vatLiable : true;
    const defaultRate = profile?.defaultVatRate !== undefined ? profile.defaultVatRate : 20;
    setDefaultVatRate(liable ? defaultRate : 0);
    setGlobalVatZero(!liable);
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        number: invoice.number,
        companyId: invoice.companyId,
        date: invoice.date,
        dueDate: invoice.dueDate,
        status: invoice.status,
        notes: invoice.notes || '',
        items: invoice.items || []
      });
      if (invoice.items && invoice.items.length > 0) {
        const mappedItems = invoice.items.map(item => ({
          id: item.id || Date.now() + Math.random(),
          description: item.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'unité',
          unitPrice: item.unitPrice || 0,
          discount: item.discount || 0,
          vatRate: item.vatRate !== undefined ? item.vatRate : 20,
          totalHT: item.totalHT || 0,
          totalTTC: item.totalTTC || 0
        }));
        setItems(mappedItems);
        const allZeroVat = mappedItems.every(item => item.vatRate === 0);
        setGlobalVatZero(allZeroVat);
      } else {
        setItems([
          {
            id: Date.now(),
            description: '',
            quantity: 1,
            unit: 'unité',
            unitPrice: 0,
            discount: 0,
            vatRate: 20,
            totalHT: 0,
            totalTTC: 0
          }
        ]);
        setGlobalVatZero(false);
      }
    } else {
      setEditingInvoice(null);
      const nextNumber = `FAC-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`;
      const today = new Date().toISOString().split('T')[0];
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30); // 30 jours par défaut
      
      setFormData({
        number: nextNumber,
        companyId: '',
        date: today,
        dueDate: dueDate.toISOString().split('T')[0],
        status: 'DRAFT',
        notes: '',
        items: []
      });

      const activity = loadActivityMode();
      const defaults = getDefaultInvoiceItemsForActivity(activity?.id, {
        defaultVatRate: liable ? defaultRate : 0,
        globalVatZero: !liable,
      });

      setItems(
        defaults.length
          ? defaults
          : [
              {
                id: Date.now(),
                description: '',
                quantity: 1,
                unit: 'unité',
                unitPrice: 0,
                discount: 0,
                vatRate: liable ? defaultRate : 0,
                totalHT: 0,
                totalTTC: 0,
              },
            ]
      );
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInvoice(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateItemTotal = (item) => {
    let subtotal = item.quantity * item.unitPrice;
    if (item.discount && item.discount > 0) {
      subtotal = subtotal * (1 - item.discount / 100);
    }
    const totalHT = subtotal;
    const totalTTC = totalHT * (1 + item.vatRate / 100);
    return { totalHT, totalTTC };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    const { totalHT, totalTTC } = calculateItemTotal(newItems[index]);
    newItems[index].totalHT = totalHT;
    newItems[index].totalTTC = totalTTC;
    setItems(newItems);
  };

  const addItem = () => {
    setItems(prev => ([
      ...prev,
      {
        id: Date.now(),
        description: '',
        quantity: 1,
        unit: 'unité',
        unitPrice: 0,
        discount: 0,
        vatRate: globalVatZero ? 0 : defaultVatRate,
        totalHT: 0,
        totalTTC: 0
      }
    ]));
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleGlobalVatZeroChange = (checked) => {
    setGlobalVatZero(checked);
    const newItems = items.map(item => ({
      ...item,
      vatRate: checked ? 0 : defaultVatRate
    }));
    setItems(newItems);
  };

  const profileCatalogItems = (loadCompanyProfile()?.catalogItems || []).map(item => ({
    ...item,
    id: item.id || `custom-${item.label}`
  }));

  const combinedCatalogs = [
    ...catalogPresets,
    { id: 'custom', label: 'Mes prestations', items: profileCatalogItems }
  ];

  const optimizedForLabel = (() => {
    switch (selectedSector) {
      case 'electrician':
        return 'Électricien indépendant';
      case 'plumber':
        return 'Plombier indépendant';
      case 'btp':
        return 'Artisan bâtiment';
      case 'freelance':
        return 'Freelance';
      case 'beauty':
        return 'Salon beauté';
      case 'commerce':
        return 'Commerce / vente';
      case 'liberal':
        return 'Profession libérale';
      case 'coach':
        return 'Coach / thérapeute';
      case 'services':
      default:
        return 'votre activité';
    }
  })();

  const currentCatalog = combinedCatalogs.find(section => section.id === selectedSector);
  const customItemsByLabel = new Map(
    profileCatalogItems.map(item => [item.label?.toLowerCase(), item])
  );
  const currentCatalogItems = currentCatalog
    ? currentCatalog.items.map(item => customItemsByLabel.get(item.label?.toLowerCase()) || item)
    : [];
  const filteredCatalogItems = currentCatalogItems.filter(item => {
    if (!catalogSearch) return true;
    const query = catalogSearch.toLowerCase();
    return (
      item.label.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  });

  const toggleCatalogItem = (itemId) => {
    setSelectedCatalogIds(prev => (
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    ));
  };

  const buildItemFromCatalog = (catalogItem) => {
    const baseItem = {
      id: Date.now() + Math.random(),
      description: catalogItem.description || catalogItem.label,
      quantity: catalogItem.quantity || 1,
      unit: catalogItem.unit || 'unité',
      unitPrice: catalogItem.unitPrice || 0,
      discount: 0,
      vatRate: globalVatZero ? 0 : (catalogItem.vatRate ?? defaultVatRate),
      totalHT: 0,
      totalTTC: 0
    };
    const totals = calculateItemTotal(baseItem);
    return { ...baseItem, ...totals };
  };

  const handleAddSelectedItems = () => {
    if (selectedCatalogIds.length === 0) return;
    const selectedItems = currentCatalogItems.filter(item => selectedCatalogIds.includes(item.id));
    const newItems = selectedItems.map(buildItemFromCatalog);
    setItems(prev => [...prev, ...newItems]);
    setSelectedCatalogIds([]);
  };

  const handleAssistantSuggest = () => {
    const suggested = suggestCatalogSector(assistantInput);
    setSelectedSector(suggested);
  };

  const handleSubmit = async () => {
    try {
      const selectedCompany = companies.find(c => c.id === formData.companyId);
      const payload = {
        ...formData,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit || 'unité',
          unitPrice: parseFloat(item.unitPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          vatRate: item.vatRate !== undefined && item.vatRate !== null ? parseFloat(item.vatRate) : 20
        }))
      };
      
      if (editingInvoice) {
        const updatedInvoice = {
          ...editingInvoice,
          ...payload,
          companyName: selectedCompany?.name
        };
        
        try {
          await invoicesService.update(editingInvoice.id, payload);
        } catch (apiError) {
          logger.debug('API non disponible, sauvegarde locale');
        }
        
        const updatedInvoices = invoices.map(invoice => 
          invoice.id === editingInvoice.id ? updatedInvoice : invoice
        );
        setInvoices(updatedInvoices);
      } else {
        // Création d'une nouvelle facture
        const response = await invoicesService.create(payload);
        const newInvoice = response.data || response;
        setInvoices(prev => [...prev, newInvoice]);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      try {
        await invoicesService.delete(invoiceId);
        const updatedInvoices = invoices.filter(invoice => invoice.id !== invoiceId);
        setInvoices(updatedInvoices);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handlePreview = (invoice) => {
    setPreviewInvoice(invoice);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setPreviewInvoice(null);
  };

  // Fonction de filtrage des factures
  const filteredInvoices = invoices.filter(invoice => {
      // Filtre par terme de recherche (numéro, client)
      if (searchFilters.searchTerm) {
        const searchLower = searchFilters.searchTerm.toLowerCase();
        const matchesSearch = 
          invoice.number?.toLowerCase().includes(searchLower) ||
          invoice.company?.name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Filtre par montant minimum
      if (searchFilters.minAmount && invoice.totalTTC < parseFloat(searchFilters.minAmount)) {
        return false;
      }

      // Filtre par montant maximum
      if (searchFilters.maxAmount && invoice.totalTTC > parseFloat(searchFilters.maxAmount)) {
        return false;
      }

      // Filtre par date de début
      if (searchFilters.dateFrom && new Date(invoice.date) < new Date(searchFilters.dateFrom)) {
        return false;
      }

      // Filtre par date de fin
      if (searchFilters.dateTo && new Date(invoice.date) > new Date(searchFilters.dateTo)) {
        return false;
      }

      // Filtre par client
      if (searchFilters.company && invoice.companyId !== searchFilters.company) {
        return false;
      }

      // Filtre par statut
      if (searchFilters.status && invoice.status !== searchFilters.status) {
        return false;
      }

      return true;
    });

  // Gestion des filtres
  const handleFilterChange = (filterName, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      searchTerm: '',
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
      company: '',
      status: ''
    });
  };

  const stats = {
    total: filteredInvoices.length,
    draft: filteredInvoices.filter(inv => inv.status === 'DRAFT').length,
    sent: filteredInvoices.filter(inv => inv.status === 'SENT').length,
    paid: filteredInvoices.filter(inv => inv.status === 'PAID').length,
    overdue: filteredInvoices.filter(inv => inv.status === 'OVERDUE' || isOverdue(inv)).length,
    totalRevenue: filteredInvoices
      .filter(inv => inv.status === 'PAID')
      .reduce((sum, inv) => sum + (inv.totalTTC || 0), 0),
    pendingAmount: invoices
      .filter(inv => inv.status === 'SENT' || inv.status === 'OVERDUE')
      .reduce((sum, inv) => sum + (inv.totalTTC || 0), 0)
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" alignItems="center">
          Gestion des Factures
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #7A7068 0%, #8B8178 52%, #5E564F 100%)',
            color: '#fff'
          }}
        >
          Nouvelle Facture
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Factures
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Payées
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.paid}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    En Retard
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {stats.overdue}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    CA Encaissé
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.totalRevenue.toFixed(0)} €
                  </Typography>
                </Box>
                <EuroIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section de recherche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Rechercher des factures
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setShowFilters(!showFilters)}
              startIcon={showFilters ? <CloseIcon /> : <SearchIcon />}
            >
              {showFilters ? 'Masquer' : 'Filtres avancés'}
            </Button>
          </Box>
          
          {/* Recherche rapide */}
          <TextField
            fullWidth
            placeholder="Rechercher par numéro ou client..."
            value={searchFilters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: showFilters ? 2 : 0 }}
          />

          {/* Filtres avancés */}
          {showFilters && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Montant min (€)"
                  type="number"
                  value={searchFilters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Montant max (€)"
                  type="number"
                  value={searchFilters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  value={searchFilters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={searchFilters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={searchFilters.company}
                    onChange={(e) => handleFilterChange('company', e.target.value)}
                    label="Client"
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    {companies.map(company => (
                      <MenuItem key={company.id} value={company.id}>
                          {company.name} {company.companyType === 'B2C' ? '(Particulier)' : '(Entreprise)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={searchFilters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Statut"
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="DRAFT">Brouillon</MenuItem>
                    <MenuItem value="SENT">Envoyé</MenuItem>
                    <MenuItem value="PENDING">En attente</MenuItem>
                    <MenuItem value="PAID">Payé</MenuItem>
                    <MenuItem value="OVERDUE">En retard</MenuItem>
                    <MenuItem value="CANCELLED">Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{ height: '56px' }}
                >
                  Réinitialiser
                </Button>
              </Grid>
            </Grid>
          )}
          
          {/* Résultats */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {filteredInvoices.length} facture(s) trouvée(s) sur {invoices.length} total
          </Typography>
        </CardContent>
      </Card>

      {/* Liste des factures */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Liste des Factures
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #7A7068 0%, #8B8178 52%, #5E564F 100%)',
                  '& .MuiTableCell-root': {
                    color: '#FFFFFF',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.22)'
                  }
                }}>
                  <TableCell><strong>Numéro</strong></TableCell>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Créé par</strong></TableCell>
                  <TableCell><strong>Échéance</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                  <TableCell><strong>Signature</strong></TableCell>
                  <TableCell align="right"><strong>Montant TTC</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow 
                    key={invoice.id} 
                    hover
                    sx={{
                      backgroundColor: isOverdue(invoice) && invoice.status !== 'PAID' 
                        ? 'error.light' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {invoice.number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.company?.name || 'Client inconnu'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(invoice.date).toLocaleDateString('fr-FR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.createdBy?.name || invoice.user?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        color={isOverdue(invoice) && invoice.status !== 'PAID' ? 'error' : 'inherit'}
                      >
                        {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(invoice.status)}
                        color={getStatusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.signatureStatus === 'SIGNED' ? 'Signée' : 'Non signée'}
                        color={invoice.signatureStatus === 'SIGNED' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.totalTTC?.toFixed(2)} €
                      </Typography>
                      {invoice.status === 'PAID' && (
                        <Typography variant="caption" color="success.main">
                          Payé
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="info"
                        onClick={() => handlePreview(invoice)}
                        title="Prévisualiser"
                      >
                        <PdfIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="success"
                        onClick={() => handleDownloadPDF(invoice.id)}
                        title="Télécharger PDF"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleExportCsv(invoice)}
                        title="Exporter CSV"
                      >
                        <CsvIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleExportExcel(invoice)}
                        title="Exporter Excel"
                      >
                        <ExcelIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenEmailDialog(invoice)}
                        title="Envoyer par email"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenSignDialog(invoice)}
                        title="Signer électroniquement"
                        disabled={invoice.signatureStatus === 'SIGNED'}
                      >
                        <SignIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(invoice)}
                        color="primary"
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(invoice.id)}
                        color="error"
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog pour ajouter/éditer une facture */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingInvoice ? 'Modifier la Facture' : 'Nouvelle Facture'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro"
                name="number"
                value={formData.number}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Client</InputLabel>
                <Select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  label="Client"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name} {company.companyType === 'B2C' ? '(Particulier)' : '(Entreprise)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date d'échéance"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Statut"
                >
                  <MenuItem value="DRAFT">Brouillon</MenuItem>
                  <MenuItem value="SENT">Envoyée</MenuItem>
                  <MenuItem value="PAID">Payée</MenuItem>
                  <MenuItem value="OVERDUE">En retard</MenuItem>
                  <MenuItem value="CANCELLED">Annulée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" color="primary">
                    Prestations
                  </Typography>
                  <FormControl component="fieldset" size="small">
                    <Box display="flex" alignItems="center">
                      <input
                        type="checkbox"
                        id="globalVatZeroInvoice"
                        checked={globalVatZero}
                        onChange={(e) => handleGlobalVatZeroChange(e.target.checked)}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <label htmlFor="globalVatZeroInvoice" style={{ cursor: 'pointer', fontSize: '14px' }}>
                        TVA à 0% (toutes les lignes)
                      </label>
                    </Box>
                  </FormControl>
                </Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  variant="outlined"
                  size="small"
                >
                  Ajouter une prestation
                </Button>
              </Box>

              <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Catalogue rapide (cases à cocher)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Catalogue optimisé pour : {optimizedForLabel}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Secteur</InputLabel>
                      <Select
                        label="Secteur"
                        value={selectedSector}
                        onChange={(e) => setSelectedSector(e.target.value)}
                      >
                        {combinedCatalogs.map(section => (
                          <MenuItem key={section.id} value={section.id}>
                            {section.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {selectedSector === 'plumber' ? (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={plumberUrgence}
                              onChange={(e) => setPlumberUrgence(e.target.checked)}
                            />
                          }
                          label="Urgence (majoration auto)"
                        />
                      </Box>
                    </Grid>
                  ) : null}

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Recherche"
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      placeholder="Ex: maintenance, site web"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Assistant (que facturez-vous ?)"
                        value={assistantInput}
                        onChange={(e) => setAssistantInput(e.target.value)}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleAssistantSuggest}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Suggérer
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {filteredCatalogItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucun résultat pour ce secteur.
                  </Typography>
                ) : (
                  <Grid container spacing={1}>
                    {filteredCatalogItems.map(item => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedCatalogIds.includes(item.id)}
                              onChange={() => toggleCatalogItem(item.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.unitPrice} € / {item.unit}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleAddSelectedItems}
                    disabled={selectedCatalogIds.length === 0}
                  >
                    Ajouter la sélection
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setSelectedCatalogIds([])}
                  >
                    Tout décocher
                  </Button>
                </Box>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      backgroundColor: '#5E564F',
                      '& .MuiTableCell-root': {
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        borderBottom: '2px solid #746960'
                      }
                    }}>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Qté</TableCell>
                      <TableCell align="center">Unité</TableCell>
                      <TableCell align="right">Prix unitaire HT</TableCell>
                      <TableCell align="center">Remise %</TableCell>
                      <TableCell align="center">TVA %</TableCell>
                      <TableCell align="right">Total HT</TableCell>
                      <TableCell align="right">Total TTC</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Décrivez la prestation"
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.5 }}
                            size="small"
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ width: 90 }}>
                            <Select
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                            >
                              <MenuItem value="unité">unité</MenuItem>
                              <MenuItem value="h">heure</MenuItem>
                              <MenuItem value="j">jour</MenuItem>
                              <MenuItem value="mois">mois</MenuItem>
                              <MenuItem value="forfait">forfait</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            size="small"
                            sx={{ width: 120 }}
                            InputProps={{ endAdornment: '€' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.discount}
                            onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            size="small"
                            sx={{ width: 80 }}
                            InputProps={{ endAdornment: '%' }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ width: 80 }}>
                            <Select
                              value={item.vatRate}
                              onChange={(e) => handleItemChange(index, 'vatRate', parseFloat(e.target.value))}
                            >
                              <MenuItem value={0}>0%</MenuItem>
                              <MenuItem value={5.5}>5,5%</MenuItem>
                              <MenuItem value={10}>10%</MenuItem>
                              <MenuItem value={20}>20%</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.totalHT.toFixed(2)} €
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.totalTTC.toFixed(2)} €
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50', minWidth: 260 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Récapitulatif
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography>Sous-total HT:</Typography>
                    <Typography>{totals.subtotalHT.toFixed(2)} €</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography>TVA:</Typography>
                    <Typography>{totals.vatAmount.toFixed(2)} €</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontWeight="bold">Total TTC:</Typography>
                    <Typography fontWeight="bold" color="primary">
                      {totals.totalTTC.toFixed(2)} €
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingInvoice ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'envoi par email */}
      <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          📧 Envoyer la facture par email
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {emailInvoice && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Facture</Typography>
                <Typography variant="h6">{emailInvoice.number}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Montant: {emailInvoice.totalTTC?.toFixed(2)} € TTC
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Email du destinataire"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
              helperText="Le PDF de la facture sera envoyé en pièce jointe"
              sx={{ mb: 2 }}
            />

            {emailSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {emailSuccess}
              </Alert>
            )}

            {emailError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {emailError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailSending}>
            Annuler
          </Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            disabled={emailSending || !recipientEmail}
            startIcon={<SendIcon />}
          >
            {emailSending ? 'Envoi en cours...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de signature électronique */}
      <Dialog open={signDialogOpen} onClose={handleCloseSignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>✍️ Signature électronique</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {signingInvoice && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Facture</Typography>
                <Typography variant="h6">{signingInvoice.number}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Montant: {signingInvoice.totalTTC?.toFixed(2)} € TTC
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Nom du signataire"
              value={signForm.name}
              onChange={(e) => setSignForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Email du signataire"
              type="email"
              value={signForm.email}
              onChange={(e) => setSignForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Rôle (optionnel)"
              value={signForm.role}
              onChange={(e) => setSignForm(prev => ({ ...prev, role: e.target.value }))}
              sx={{ mb: 2 }}
            />

            {signSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {signSuccess}
              </Alert>
            )}

            {signError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {signError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSignDialog} disabled={signLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSignInvoice}
            variant="contained"
            disabled={signLoading || !signForm.name || !signForm.email}
            startIcon={<SignIcon />}
          >
            {signLoading ? 'Signature...' : 'Signer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de prévisualisation */}
      <InvoicePreviewDialog
        open={openPreview}
        onClose={handleClosePreview}
        invoice={previewInvoice}
        company={previewInvoice && companies.find(c => c.id === previewInvoice.companyId)}
      />
    </Box>
  );
};

export default Invoices;